// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { avoidBreakInsidePrintElement, xScreen } from "@conklin/XDL/Utilities";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

interface OrderInformationStyles {
    label?: TypographyProps;
    value?: TypographyProps;
    labelWithDots?: TypographyProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    address?: { heading?: TypographyProps; informationTitle?: TypographyProps } & AddressInfoDisplayStyles;
    note?: { wrapper?: InjectableCss; title?: TypographyProps; text?: TypographyProps };
    orderSummary?: any;
}

const xOrderInformationStyles: OrderInformationStyles = {
    label: {
        variant: "h6",
        css: css`
            margin-bottom: 0;
            @media print {
                display: block;
            }
        `,
    },
    value: {
        size: 14,
        lineHeight: "17px",
        css: css`
            &:empty:after {
                content: "- -";
            }
            @media print {
                display: inline-block;
                width: auto;
            }
        `,
    },
    address: {
        informationTitle: {
            variant: "h6",
            as: "h2",
            css: css`
                margin-bottom: 8px;
            `,
        },
        heading: {
            variant: "h3",
            as: "h1",
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    note: {
        text: {
            size: 14,
            lineHeight: "22px",
        },
    },
    orderSummary: {
        css: css`
            ${avoidBreakInsidePrintElement}
            ${xScreen("lte", "md")} {
                margin-bottom: 10px;
            }
        `,
    },
};

xOrderInformationStyles.labelWithDots = {
    variant: "h6",
    transform: "none",
    css: css`
        margin-right: 8px;
        margin-bottom: 0;
        white-space: nowrap;
        &:after {
            content: ":";
        }
        @media print {
            display: inline-block;
            width: auto;
        }
    `,
};

xOrderInformationStyles.headingAndTextStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            @media print {
                display: block;
            }
        `,
    },
    heading: {
        ...xOrderInformationStyles.label,
    },
    text: {
        ...xOrderInformationStyles.value,
    },
};

export default xOrderInformationStyles;
